import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { debounce } from 'src/components/muiTheme/utils';

type MessageTypes = 'sendText' | 'close' | 'hideNav' | 'showNav';

interface NativeBridgeValue {
  darkMode: boolean | null;
  isWebview: boolean | null;
  isInIframe: boolean;
  closeable: boolean | null;
  inset: {
    top: number;
    bottom: number;
  };
  postMessage: (t: MessageTypes, v?: string) => void;
  authToken?: string;
  supportsNativeDrawerToggle: () => boolean;
  toggleNativeDrawer: () => void;
  supportsTapToPay: () => Promise<boolean>;
  openTapToPay: (params: {
    paymentIntentSecret?: string;
    setupIntentSecret?: string;
  }) => Promise<boolean>;
  supportsTerraSdk: () => Promise<'samsung' | 'ios' | 'android' | null>;
  connectTerraSdk: () => Promise<{
    success: boolean;
    userId: string;
    errorCode?: string;
    errorMessage?: string;
  }>;
  setOnVisibleCallback: (callback: () => void) => () => void;
  nativeBridgeAvailable: boolean;
  closeWebview: () => void;
}

const NativeBridge = createContext({});
const anyWindow = window as any;

const hasPostMessage = (
  bridge: unknown
): bridge is { postMessage: (...args: unknown[]) => unknown } =>
  typeof (bridge as { postMessage?: unknown } | null)?.postMessage ===
  'function';

const NativeBridgeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [searchParams] = useSearchParams();

  const [params] = useState({
    darkMode: searchParams.get('darkmode'),
    insetTop: searchParams.get('insetTop'),
    insetBottom: searchParams.get('insetBottom'),
    isWebview: searchParams.get('isWebview'),
    closeable: searchParams.get('closeable'),
    authToken: searchParams.get('auth_token'),
    noChrome: searchParams.get('nochrome'),
  });

  // State to track native bridge availability
  const [nativeBridgeAvailable, setNativeBridgeAvailable] = useState(false);

  const isInIframe = useMemo(() => {
    try {
      return window.self !== window.top;
    } catch (e) {
      // If we can't access window.top due to cross-origin restrictions,
      // we're probably in an iframe
      return true;
    }
  }, []);

  const [inset] = useState({
    top: Number(params.insetTop) || 0,
    bottom: Number(params.insetBottom) || 0,
  });

  const hasWebkitBridge = useCallback(() => {
    return (
      anyWindow.webkit &&
      anyWindow.webkit.messageHandlers &&
      (anyWindow.webkit.messageHandlers.exercise ||
        anyWindow.webkit.messageHandlers.modal)
    );
  }, []);

  const hasAndroidBridge = useCallback(() => {
    return hasPostMessage(anyWindow.androidBridge);
  }, []);

  const hasNativeBridge = useCallback(() => {
    return anyWindow.nativeBridge != null;
  }, []);

  const closeable = useMemo(() => {
    return params.closeable == 'true';
  }, [params]);

  const authToken = params.authToken || '';

  const isWebview = useMemo(() => {
    return hasWebkitBridge() || hasAndroidBridge() || !!params.isWebview;
  }, [hasAndroidBridge, hasWebkitBridge, params]);

  const darkModeParam =
    params.darkMode !== null ? params.darkMode === 'true' : null;
  const darkMode =
    darkModeParam ||
    (isWebview &&
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches)
      ? true
      : null;

  const getWebkitBridge = useCallback(() => {
    if (hasWebkitBridge()) {
      return (
        // modal is legacy
        anyWindow.webkit.messageHandlers.exercise ||
        anyWindow.webkit.messageHandlers.modal
      );
    }
    return undefined;
  }, [hasWebkitBridge]);

  const getAndroidBridge = useCallback(() => {
    if (hasAndroidBridge()) {
      return anyWindow.androidBridge;
    }
    return undefined;
  }, [hasAndroidBridge]);

  const getNativeBridge = useCallback(() => {
    if (hasNativeBridge()) {
      return anyWindow.nativeBridge;
    }
    return undefined;
  }, [hasNativeBridge]);

  const supportsNativeDrawerToggle = useCallback(() => {
    return hasNativeBridge() || nativeBridgeAvailable;
  }, [hasNativeBridge, nativeBridgeAvailable]);

  const toggleNativeDrawer = useCallback(async () => {
    const nativeBridge = getNativeBridge();
    if (nativeBridge) {
      const isOpen = await nativeBridge.isDrawerOpen();
      if (isOpen) {
        await nativeBridge.closeDrawer();
      } else {
        await nativeBridge.openDrawer();
      }
      return;
    }

    throw new Error('Attempted to toggle drawer (in prod) when not in webview');
  }, [getNativeBridge]);

  const supportsTapToPay = useCallback(async (): Promise<boolean> => {
    let supports = false;
    const bridge = getNativeBridge();

    if (bridge) {
      supports = await bridge.supportsTapToPay();
    }

    return supports;
  }, [getNativeBridge]);

  const openTapToPay = useCallback(
    async ({
      paymentIntentSecret,
      setupIntentSecret,
    }: {
      paymentIntentSecret?: string;
      setupIntentSecret?: string;
    }): Promise<boolean> => {
      let result = false;

      const bridge = getNativeBridge();

      if (bridge) {
        result = await bridge.openTapToPay({
          paymentIntentSecret,
          setupIntentSecret,
        });
      }

      return result;
    },
    [getNativeBridge]
  );

  const supportsTerraSdk = useCallback(async (): Promise<
    'samsung' | 'ios' | 'android' | null
  > => {
    const bridge = getNativeBridge();

    if (bridge && bridge.supportsTerraSdk) {
      return await bridge.supportsTerraSdk();
    }

    return null;
  }, [getNativeBridge]);

  const connectTerraSdk = useCallback(async (): Promise<{
    success: boolean;
    userId: string;
    errorCode?: string;
    errorMessage?: string;
  }> => {
    const bridge = getNativeBridge();

    if (bridge && bridge.connectTerraSdk) {
      const result = await bridge.connectTerraSdk();

      return {
        success: result.success ?? false,
        userId: result.userId ?? '',
        errorCode: result.errorCode,
        errorMessage: result.errorMessage,
      };
    }

    return { success: false, userId: '' };
  }, [getNativeBridge]);

  const postMessage = useCallback(
    (type: MessageTypes, text?: string) => {
      const webkitBridge = getWebkitBridge();
      const androidBridge = getAndroidBridge();

      if (webkitBridge) {
        webkitBridge.postMessage({
          message: type,
          text,
        });
        return;
      }
      if (androidBridge) {
        androidBridge.postMessage(type, text);
        return;
      }
      if (type === 'hideNav') {
        return;
      }

      throw new Error('Attempted to postMessage (in prod) when not in webview');
    },
    [getAndroidBridge, getWebkitBridge]
  );

  const closeWebview = useCallback(() => {
    const nativeBridge = getNativeBridge();
    if (nativeBridge) {
      postMessage('close');
    } else {
      window.close();
    }
  }, [getNativeBridge, postMessage]);

  // Helper function to set up visibility event listeners
  const setupVisibilityListeners = useCallback(
    (callback: () => void, debounceMs = 300) => {
      if (!isWebview) {
        return () => {};
      }

      const debouncedCallback = debounce(callback, debounceMs);

      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          debouncedCallback();
        }
      };

      const handlePageShow = () => {
        debouncedCallback();
      };

      const handleFocus = () => {
        debouncedCallback();
      };

      // Set up event listeners
      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('pageshow', handlePageShow);
      window.addEventListener('focus', handleFocus);

      // Return cleanup function
      return () => {
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange
        );
        window.removeEventListener('pageshow', handlePageShow);
        window.removeEventListener('focus', handleFocus);
        debouncedCallback.clear();
      };
    },
    [isWebview]
  );

  const setOnVisibleCallback = useCallback(
    (callback: () => void) => {
      return setupVisibilityListeners(callback, 300);
    },
    [setupVisibilityListeners]
  );

  // Check for native bridge availability on visibility changes
  useEffect(() => {
    if (!isWebview) return;

    const checkNativeBridge = () => {
      setNativeBridgeAvailable(hasNativeBridge());
    };

    // Check immediately
    checkNativeBridge();

    // Set up listeners with shorter debounce for internal bridge checking
    return setupVisibilityListeners(checkNativeBridge, 100);
  }, [hasNativeBridge, isWebview, setupVisibilityListeners]);

  const contextValue = useMemo(() => {
    return {
      isWebview,
      isInIframe,
      darkMode,
      inset,
      postMessage,
      closeable,
      authToken,
      supportsNativeDrawerToggle,
      toggleNativeDrawer,
      supportsTapToPay,
      openTapToPay,
      supportsTerraSdk,
      connectTerraSdk,
      setOnVisibleCallback,
      nativeBridgeAvailable,
      closeWebview,
    } as NativeBridgeValue;
  }, [
    isWebview,
    isInIframe,
    darkMode,
    inset,
    postMessage,
    closeable,
    authToken,
    supportsNativeDrawerToggle,
    toggleNativeDrawer,
    supportsTapToPay,
    openTapToPay,
    supportsTerraSdk,
    connectTerraSdk,
    setOnVisibleCallback,
    nativeBridgeAvailable,
    closeWebview,
  ]);

  useEffect(() => {
    // Don't hide nav if we're in an iframe (disabled mode)
    if (isWebview && !params.noChrome && !isInIframe) {
      postMessage('hideNav');
    }

    const handleVisibilityChange = debounce(() => {
      if (document.visibilityState === 'visible' && isWebview && !isInIframe) {
        postMessage('hideNav');
      }
    }, 150); // Debounce by 150ms

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      handleVisibilityChange.clear(); // Clear the debounce timeout if necessary
    };
  }, [postMessage, params.noChrome, isWebview, isInIframe]);

  return (
    <NativeBridge.Provider value={contextValue}>
      {isWebview && (
        <Box id="webview-wrapper" sx={{ height: '100dvh' }}>
          {children}
        </Box>
      )}
      {!isWebview && children}
    </NativeBridge.Provider>
  );
};

function useNativeBridge() {
  const context = useContext(NativeBridge);
  return context as NativeBridgeValue;
}

export { NativeBridgeProvider, useNativeBridge };
